import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '@/api/client';
import type {
  AccessResponse,
  AdminOverview,
  AdminReports,
  AdProduct,
  Advertiser,
  AdvertiserAdmin,
  Availability,
  Booking,
  Campaign,
  Creative,
  Daypart,
  InventoryRow,
  Invoice,
  Me,
  Paginated,
  RateEntry,
  ReportsSummary,
  Transaction,
  Wallet,
} from './types';

// --------------------------------------------------------------------------- //
// Advertiser self-serve
// --------------------------------------------------------------------------- //
export const useMe = () => useQuery({ queryKey: ['ads', 'me'], queryFn: () => get<Me>('/api/ads/me/') });

export const useRateCard = () =>
  useQuery({ queryKey: ['ads', 'rate-card'], queryFn: () => get<AdProduct[]>('/api/ads/rate-card/') });

export const useDayparts = () =>
  useQuery({ queryKey: ['ads', 'dayparts'], queryFn: () => get<Daypart[]>('/api/ads/dayparts/') });

export const useAdminDayparts = () =>
  useQuery({ queryKey: ['ads', 'admin', 'dayparts'], queryFn: () => get<Daypart[]>('/api/ads/admin/dayparts/') });

export function useAvailability(params: {
  product?: number;
  daypart?: number | null;
  start?: string;
  end?: string;
  slots?: number;
}) {
  const enabled = !!params.product && !!params.start && !!params.end;
  return useQuery({
    queryKey: ['ads', 'availability', params],
    enabled,
    queryFn: () =>
      get<Availability>('/api/ads/availability/', {
        params: {
          product: params.product,
          daypart: params.daypart ?? undefined,
          start: params.start,
          end: params.end,
          slots: params.slots ?? 1,
        },
      }),
  });
}

export const useCampaigns = () =>
  useQuery({
    queryKey: ['ads', 'campaigns'],
    queryFn: () => get<Paginated<Campaign>>('/api/ads/campaigns/', { params: { page_size: 100 } }),
  });

export const useCampaign = (id: number | undefined) =>
  useQuery({
    queryKey: ['ads', 'campaign', id],
    enabled: !!id,
    queryFn: () => get<Campaign>(`/api/ads/campaigns/${id}/`),
  });

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; objective?: string }) => post<Campaign>('/api/ads/campaigns/', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ads', 'campaigns'] }),
  });
}

export function useBookings(params: { campaign?: number; status?: string } = {}) {
  return useQuery({
    queryKey: ['ads', 'bookings', params],
    queryFn: () =>
      get<Paginated<Booking>>('/api/ads/bookings/', { params: { ...params, page_size: 100 } }),
  });
}

export const useBooking = (id: number | undefined) =>
  useQuery({
    queryKey: ['ads', 'booking', id],
    enabled: !!id,
    queryFn: () => get<Booking>(`/api/ads/bookings/${id}/`),
  });

export interface CreateBookingBody {
  campaign: number;
  product: number;
  daypart?: number | null;
  target_store?: number | null;
  start_date: string;
  end_date: string;
  slots_requested: number;
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBookingBody) => post<Booking>('/api/ads/bookings/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'bookings'] });
      qc.invalidateQueries({ queryKey: ['ads', 'campaigns'] });
    },
  });
}

export function useSubmitBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => post<Booking>(`/api/ads/bookings/${id}/submit/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'bookings'] });
      qc.invalidateQueries({ queryKey: ['ads', 'wallet'] });
      qc.invalidateQueries({ queryKey: ['ads', 'me'] });
    },
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => post<Booking>(`/api/ads/bookings/${id}/cancel/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'bookings'] });
      qc.invalidateQueries({ queryKey: ['ads', 'wallet'] });
    },
  });
}

export const useCreatives = (bookingId: number | undefined) =>
  useQuery({
    queryKey: ['ads', 'creatives', bookingId],
    enabled: !!bookingId,
    queryFn: () => get<Creative[]>('/api/ads/creatives/', { params: { booking: bookingId } }),
  });

export function useCreateCreative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) =>
      post<Creative>('/api/ads/creatives/', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
    onSuccess: (_data, form) => {
      const bookingId = form.get('booking');
      qc.invalidateQueries({ queryKey: ['ads', 'creatives', Number(bookingId)] });
      qc.invalidateQueries({ queryKey: ['ads', 'bookings'] });
    },
  });
}

export function useDeleteCreative(bookingId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del(`/api/ads/creatives/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ads', 'creatives', bookingId] }),
  });
}

export const useWallet = () =>
  useQuery({ queryKey: ['ads', 'wallet'], queryFn: () => get<Wallet>('/api/ads/wallet/') });

export const useTransactions = () =>
  useQuery({
    queryKey: ['ads', 'transactions'],
    queryFn: () => get<Paginated<Transaction>>('/api/ads/wallet/transactions/', { params: { page_size: 100 } }),
  });

export function useTopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: number; reference?: string }) => post('/api/ads/wallet/topup/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'wallet'] });
      qc.invalidateQueries({ queryKey: ['ads', 'transactions'] });
      qc.invalidateQueries({ queryKey: ['ads', 'me'] });
    },
  });
}

export const useInvoices = () =>
  useQuery({
    queryKey: ['ads', 'invoices'],
    queryFn: () => get<Paginated<Invoice>>('/api/ads/invoices/', { params: { page_size: 100 } }),
  });

export const useReportsSummary = () =>
  useQuery({ queryKey: ['ads', 'reports-summary'], queryFn: () => get<ReportsSummary>('/api/ads/reports/summary/') });

export const useUpdateAdvertiser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Advertiser>) => patch<Advertiser>('/api/ads/me/', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ads', 'me'] }),
  });
};

// --------------------------------------------------------------------------- //
// Admin
// --------------------------------------------------------------------------- //
export const useAdminOverview = () =>
  useQuery({ queryKey: ['ads', 'admin', 'overview'], queryFn: () => get<AdminOverview>('/api/ads/admin/overview/') });

export const useApprovalQueue = () =>
  useQuery({
    queryKey: ['ads', 'admin', 'approvals'],
    queryFn: () => get<Paginated<Booking>>('/api/ads/admin/approvals/', { params: { page_size: 100 } }),
  });

export const useAdminBookings = (params: { status?: string; advertiser?: number } = {}) =>
  useQuery({
    queryKey: ['ads', 'admin', 'bookings', params],
    queryFn: () => get<Paginated<Booking>>('/api/ads/admin/bookings/', { params: { ...params, page_size: 100 } }),
  });

export function useReviewBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; action: 'approve' | 'reject'; reason?: string }) =>
      post<Booking>(`/api/ads/admin/bookings/${args.id}/review/`, { action: args.action, reason: args.reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'approvals'] });
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'bookings'] });
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'overview'] });
    },
  });
}

export function useReviewCreative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; action: 'approve' | 'reject'; reason?: string }) =>
      post<Creative>(`/api/ads/admin/creatives/${args.id}/review/`, { action: args.action, reason: args.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ads', 'admin', 'approvals'] }),
  });
}

export const useAdvertisers = (q?: string) =>
  useQuery({
    queryKey: ['ads', 'admin', 'advertisers', q],
    queryFn: () =>
      get<Paginated<AdvertiserAdmin>>('/api/ads/admin/advertisers/', { params: { q, page_size: 100 } }),
  });

export const useAdvertiserDetail = (id: number | undefined) =>
  useQuery({
    queryKey: ['ads', 'admin', 'advertiser', id],
    enabled: !!id,
    queryFn: () => get<AdvertiserAdmin>(`/api/ads/admin/advertisers/${id}/`),
  });

export const useAdminProducts = () =>
  useQuery({ queryKey: ['ads', 'admin', 'products'], queryFn: () => get<AdProduct[]>('/api/ads/admin/products/') });

export type AdProductInput = Pick<
  AdProduct,
  'code' | 'name' | 'description' | 'surface' | 'billing_unit' | 'default_total_slots' | 'requires_daypart' | 'is_active' | 'sort_order'
>;

export function useSaveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AdProductInput> & { id?: number }) =>
      body.id
        ? patch<AdProduct>(`/api/ads/admin/products/${body.id}/`, body)
        : post<AdProduct>('/api/ads/admin/products/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['ads', 'rate-card'] });
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del(`/api/ads/admin/products/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['ads', 'rate-card'] });
    },
  });
}

export type DaypartInput = Pick<Daypart, 'code' | 'label' | 'start_time' | 'end_time' | 'sort_order' | 'is_active'>;

export function useSaveDaypart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DaypartInput> & { id?: number }) =>
      body.id
        ? patch<Daypart>(`/api/ads/admin/dayparts/${body.id}/`, body)
        : post<Daypart>('/api/ads/admin/dayparts/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'dayparts'] });
      qc.invalidateQueries({ queryKey: ['ads', 'dayparts'] });
    },
  });
}

export function useDeleteDaypart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del(`/api/ads/admin/dayparts/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'dayparts'] });
      qc.invalidateQueries({ queryKey: ['ads', 'dayparts'] });
    },
  });
}

export const useRateEntries = (product?: number) =>
  useQuery({
    queryKey: ['ads', 'admin', 'rate-entries', product],
    queryFn: () => get<RateEntry[]>('/api/ads/admin/rate-entries/', { params: { product } }),
  });

export function useSaveRateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<RateEntry> & { id?: number }) =>
      body.id
        ? patch<RateEntry>(`/api/ads/admin/rate-entries/${body.id}/`, body)
        : post<RateEntry>('/api/ads/admin/rate-entries/', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'rate-entries'] });
      qc.invalidateQueries({ queryKey: ['ads', 'admin', 'products'] });
    },
  });
}

export function useDeleteRateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del(`/api/ads/admin/rate-entries/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ads', 'admin', 'rate-entries'] }),
  });
}

export const useAdminInventory = (params: { start?: string; end?: string } = {}) =>
  useQuery({
    queryKey: ['ads', 'admin', 'inventory', params],
    queryFn: () => get<{ start: string; end: string; rows: InventoryRow[] }>('/api/ads/admin/inventory/', { params }),
  });

export const useAdminReports = () =>
  useQuery({ queryKey: ['ads', 'admin', 'reports'], queryFn: () => get<AdminReports>('/api/ads/admin/reports/') });

export const useAdsAccess = (enabled: boolean) =>
  useQuery({
    queryKey: ['ads', 'admin', 'access'],
    enabled,
    queryFn: () => get<AccessResponse>('/api/ads/admin/access/'),
  });
